import { Image, View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { AnyGame, FootballEvent, FootballGame, NBAGame, NFLGame } from '@/types';
import { Colors, SportColors } from '@/constants/colors';
import { LiveBadge } from './LiveBadge';
import { useFavoritesStore } from '@/store/favorites';
import { gameCache } from '@/store/gameCache';

interface Props {
  game: AnyGame;
  compact?: boolean;
}

function eventIcon(event: FootballEvent): keyof typeof Ionicons.glyphMap {
  switch (event.type) {
    case 'goal':
    case 'penalty':
    case 'own_goal':
      return 'football-outline';
    case 'red_card':
    case 'yellow_card':
      return 'card-outline';
    case 'substitution':
      return 'swap-horizontal-outline';
    default:
      return 'ellipse-outline';
  }
}

function eventLabel(event: FootballEvent, game: FootballGame): string {
  const team = event.team === 'home' ? game.homeTeam.shortName : game.awayTeam.shortName;
  const player = event.player ? ` ${event.player}` : '';
  return `${event.minute}' ${team}${player}`;
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDate(iso: string) {
  try {
    const date = new Date(iso);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Hoje';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}

export function MatchCard({ game, compact = false }: Props) {
  const router = useRouter();
  const isFavorite = useFavoritesStore((s) => s.isFavorite);
  const isLive = game.status === 'live' || game.status === 'halftime';
  const isFinished = game.status === 'finished';
  const sportColor = SportColors[game.sport];
  const favHome = isFavorite(game.homeTeam.id, game.sport);
  const favAway = isFavorite(game.awayTeam.id, game.sport);
  const footballEvents =
    game.sport === 'futebol' ? ((game as FootballGame).events ?? []).slice(-2).reverse() : [];

  function handlePress() {
    gameCache.set(game);
    router.push(`/match/${game.id}?sport=${game.sport}`);
  }

  const leagueName = game.sport === 'futebol' ? (game as FootballGame).league.name : game.sport.toUpperCase();
  const leagueLogo = game.sport === 'futebol' ? (game as FootballGame).league.logo : undefined;

  return (
    <TouchableOpacity
      style={[
        styles.card,
        compact && styles.cardCompact,
        isLive && { borderColor: sportColor.primary, shadowColor: sportColor.primary },
      ]}
      onPress={handlePress}
      activeOpacity={0.78}
    >
      <View style={[styles.topAccent, { backgroundColor: isLive ? sportColor.primary : Colors.border }]} />

      <View style={styles.leagueRow}>
        <View style={styles.leagueLeft}>
          {leagueLogo ? <Image source={{ uri: leagueLogo }} style={styles.leagueLogo} resizeMode="contain" /> : null}
          <Text style={styles.leagueName} numberOfLines={1}>
            {leagueName}
          </Text>
        </View>
        <View style={styles.rightMeta}>
          {isLive ? (
            <View style={styles.livePill}>
              <View style={styles.liveDot} />
              <Text style={styles.livePillText}>AO VIVO</Text>
            </View>
          ) : (
            <Text style={styles.dateText}>{formatDate(game.startTime)}</Text>
          )}
          {(favHome || favAway) && <Ionicons name="star" size={16} color="#facc15" />}
        </View>
      </View>

      <View style={styles.scoreRow}>
        <View style={styles.teamBlock}>
          <Image source={{ uri: game.homeTeam.logo }} style={styles.teamLogo} resizeMode="contain" />
          <Text style={[styles.teamName, favHome && { color: sportColor.primary }]} numberOfLines={1}>
            {game.homeTeam.shortName}
          </Text>
        </View>

        <View style={styles.centerBlock}>
          {isLive || isFinished ? (
            <View style={styles.scoreNumbers}>
              <Text style={styles.scoreText}>{game.score.home}</Text>
              <Text style={styles.scoreDash}>-</Text>
              <Text style={styles.scoreText}>{game.score.away}</Text>
            </View>
          ) : (
            <Text style={styles.timeText}>{formatTime(game.startTime)}</Text>
          )}

          {isLive ? (
            <LiveBadge
              sport={game.sport}
              minute={(game as FootballGame).minute}
              period={(game as NBAGame).period ?? (game as NFLGame).quarter}
              clock={(game as NBAGame | NFLGame).clock}
              isHalftime={game.status === 'halftime'}
            />
          ) : (
            <Text style={styles.statusText}>{isFinished ? 'Encerrado' : formatDate(game.startTime)}</Text>
          )}
        </View>

        <View style={styles.teamBlock}>
          <Image source={{ uri: game.awayTeam.logo }} style={styles.teamLogo} resizeMode="contain" />
          <Text style={[styles.teamName, favAway && { color: sportColor.primary }]} numberOfLines={1}>
            {game.awayTeam.shortName}
          </Text>
        </View>
      </View>

      {game.sport === 'futebol' && footballEvents.length > 0 && (
        <View style={styles.eventsStrip}>
          {footballEvents.map((event, index) => (
            <View key={`${event.minute}-${event.type}-${event.player ?? 'event'}-${index}`} style={styles.eventChip}>
              <Ionicons name={eventIcon(event)} size={13} color={event.type === 'yellow_card' ? '#facc15' : sportColor.primary} />
              <Text style={styles.eventChipText} numberOfLines={1}>
                {eventLabel(event, game as FootballGame)}
              </Text>
            </View>
          ))}
        </View>
      )}

      {game.sport === 'nfl' && isLive && (game as NFLGame).down && (
        <Text style={styles.nflSituation}>
          {(game as NFLGame).down} tentativa - {(game as NFLGame).yardsToGo} jardas
        </Text>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.card,
    borderRadius: 14,
    padding: 13,
    marginHorizontal: 16,
    marginVertical: 6,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
    overflow: 'hidden',
    shadowOpacity: 0.14,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 3,
  },
  cardCompact: {
    marginHorizontal: 0,
  },
  topAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
  },
  leagueRow: {
    minHeight: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  },
  leagueLeft: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  leagueLogo: { width: 16, height: 16 },
  leagueName: {
    flex: 1,
    color: Colors.textSecondary,
    fontSize: 11,
    fontWeight: '700',
  },
  rightMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  dateText: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: '800',
  },
  livePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 8,
    backgroundColor: 'rgba(255,77,109,0.12)',
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.live,
  },
  livePillText: {
    color: Colors.live,
    fontSize: 9,
    fontWeight: '900',
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  teamBlock: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: 7,
  },
  teamLogo: { width: 44, height: 44 },
  teamName: {
    width: '100%',
    color: Colors.text,
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'center',
  },
  centerBlock: {
    width: 112,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
  scoreNumbers: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  scoreText: {
    color: Colors.text,
    fontSize: 32,
    lineHeight: 37,
    fontWeight: '900',
    fontVariant: ['tabular-nums'],
  },
  scoreDash: {
    color: Colors.textSecondary,
    fontSize: 24,
    fontWeight: '700',
  },
  timeText: {
    color: Colors.text,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: '900',
    fontVariant: ['tabular-nums'],
  },
  statusText: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: '800',
  },
  eventsStrip: {
    marginTop: 12,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
    gap: 6,
  },
  eventChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  eventChipText: {
    color: Colors.textSecondary,
    fontSize: 11,
    fontWeight: '700',
    flex: 1,
  },
  nflSituation: {
    color: Colors.textSecondary,
    fontSize: 10,
    textAlign: 'center',
    marginTop: 9,
  },
});
